import { useCallback, useEffect, useMemo, useState } from 'react';

import { initializeRadarIfNeeded, setRadarUserId } from '@sg/radar-fe';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import {
  useCustomerData,
  useHasLoggedOut,
  useIsLoggedIn,
  useIsLoggingIn,
} from '../../context/AuthMachine';
import { useBraze } from '../../context/Braze';
import { useSnapPixel } from '../../integrations/snap-pixel/hooks/useSnapPixel';
import { useRadarForegroundTracking } from '../useRadarForegroundTracking';
import { useSyncRadarMarketingPushTags } from '../useSyncRadarMarketingPushTags';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initializes third-party vendors integrations and synchronizes current customer data
 */
export const use3rdPartyIntegrations = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const hasLoggedOut = useHasLoggedOut();

  const isMixpanelFeatureFlagsEnabled = useFeatureFlag('CELS-5761-order-mixpanel-feature-flags-enabled'); // prettier-ignore

  // ─── State ───────────────────────────────────────────────────────────

  const [isRadarReady, setIsRadarReady] = useState(false);

  // ─── Derived ─────────────────────────────────────────────────────────

  const customerData = useCustomerData();
  const { id, trackingUuid, firstName, lastName, email, phoneNumber } = customerData ?? {}; // prettier-ignore

  // NOTE: We extract required customer data for 3rd party vendors to prevent
  //       extra effects
  const customer = useMemo(
    () => ({ id, trackingUuid, firstName, lastName, email, phoneNumber }),
    [id, trackingUuid, firstName, lastName, email, phoneNumber],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  // NOTE: WE extract helpers individually to prevent unnecessary effects
  //       because of internal state updates

  const {
    isInitialized: isTelemetryInitialized,
    initialize: initTelemetry,
    identify: identifyTelemetryCustomer,
    refreshMixpanelFeatureFlags,
    reset: resetTelemetryCustomer,
  } = useTelemetry();

  const { init: initBraze, destroy: destroyBraze, syncCustomer: syncBrazeCustomer } = useBraze();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const refreshTelemetryFeatureFlags = useCallback(async () => {
    if (!isMixpanelFeatureFlagsEnabled) return;

    await refreshMixpanelFeatureFlags();
  }, [isMixpanelFeatureFlagsEnabled, refreshMixpanelFeatureFlags]);

  const syncTelemetryCustomer = useCallback(async () => {
    if (!customer.id) {
      await refreshTelemetryFeatureFlags();

      return;
    }

    await identifyTelemetryCustomer(customer);
    await refreshTelemetryFeatureFlags();
  }, [customer, identifyTelemetryCustomer, refreshTelemetryFeatureFlags]);

  // ─── Telemetry ───────────────────────────────────────────────────────

  useEffect(() => {
    void initTelemetry({ isMixpanelFeatureFlagsEnabled });
  }, [initTelemetry, isMixpanelFeatureFlagsEnabled]);

  useEffect(() => {
    if (!hasLoggedOut) return;

    resetTelemetryCustomer();
  }, [hasLoggedOut, resetTelemetryCustomer]);

  useEffect(() => {
    if (!isTelemetryInitialized) return;

    void syncTelemetryCustomer();
  }, [isTelemetryInitialized, syncTelemetryCustomer, resetTelemetryCustomer, customer.id]);

  // ─── Snap Pixel ──────────────────────────────────────────────────────

  const { init: initSnapPixel } = useSnapPixel();

  useEffect(() => {
    if (isLoggingIn) return;

    initSnapPixel(customer.id ? { firstName, lastName, email, phoneNumber } : undefined);
  }, [customer, email, firstName, initSnapPixel, isLoggingIn, lastName, phoneNumber]);

  // ─── Braze ───────────────────────────────────────────────────────────

  useEffect(() => {
    initBraze?.();

    return () => {
      destroyBraze?.();
    };
  }, [destroyBraze, initBraze]);

  useEffect(() => {
    if (!customer.id) return;

    syncBrazeCustomer(customer);
  }, [customer, syncBrazeCustomer]);

  // ─── Radar ────────────────────────────────────────────────────────────

  useEffect(() => {
    void initializeRadarIfNeeded(RADAR_SDK_KEY).then(() => {
      setIsRadarReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isRadarReady || !customer.trackingUuid) return;

    setRadarUserId(customer.trackingUuid);
  }, [customer.trackingUuid, isRadarReady]);

  useRadarForegroundTracking({
    isCustomerLoggedIn: isLoggedIn,
    isReady: isRadarReady && !isLoggingIn,
  });
  useSyncRadarMarketingPushTags({ isReady: isRadarReady });
};

// ─── Constants ───────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();
